/**
 * Bing Web Search Adapter
 * Implementation for Microsoft Bing Web Search API (Azure Cognitive Services)
 * https://www.microsoft.com/en-us/bing/apis/bing-web-search-api
 */

import type { IWebSearchAdapter, WebSearchOptions } from './IWebSearchAdapter';
import type {
  BingSearchConfig,
  WebSearchResponse,
  WebSearchResult,
  RateLimitInfo,
} from '@/types/webSearch.types';
import { getBackendUrl, getAuthHeaders, prepareWebSearchQuery } from './webSearchUtils';

const BING_DEFAULT_ENDPOINT = 'https://api.bing.microsoft.com/v7.0/search';

interface BingWebPage {
  name?: string;
  url?: string;
  snippet?: string;
  dateLastCrawled?: string;
}

interface BingApiResponse {
  webPages?: {
    value?: BingWebPage[];
    totalEstimatedMatches?: number;
  };
}

export class BingAdapter implements IWebSearchAdapter {
  public readonly provider = 'bing';
  private config: BingSearchConfig | null = null;

  /**
   * Initialize the Bing adapter with configuration
   */
  async initialize(config: BingSearchConfig): Promise<void> {
    if (!config.subscriptionKey || config.subscriptionKey.trim() === '') {
      throw new Error('Bing subscription key is required');
    }

    if (config.provider !== 'bing') {
      throw new Error(`Invalid provider: expected 'bing', got '${config.provider}'`);
    }

    this.config = config;
  }

  /**
   * Execute a web search using Bing API via backend proxy
   */
  async search(query: string, options?: WebSearchOptions): Promise<WebSearchResponse> {
    if (!this.config) {
      throw new Error('Adapter not initialized. Call initialize() first.');
    }

    if (!query || query.trim() === '') {
      throw new Error('Search query cannot be empty');
    }

    const startTime = Date.now();

    try {
      // Call backend proxy to avoid CORS issues
      const response = await fetch(`${getBackendUrl()}/web-search`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          provider: 'bing',
          query: prepareWebSearchQuery(query, 'bing'),
          originalQueryLength: query.trim().length,
          config: {
            subscriptionKey: this.config.subscriptionKey,
            endpoint: this.config.endpoint || BING_DEFAULT_ENDPOINT,
            maxResults: this.config.maxResults || 10,
            market: this.config.market || 'en-US',
            safeSearch: this.config.safeSearch || 'Moderate',
          },
        }),
        signal: options?.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Bing API error (${response.status}): ${errorText || response.statusText}`
        );
      }

      const data = await response.json() as BingApiResponse;

      // Normalize Bing response to our standard format
      const webPages = data.webPages?.value || [];
      const results: WebSearchResult[] = webPages.map((result: BingWebPage) => ({
        title: result.name || '',
        url: result.url || '',
        snippet: result.snippet || '',
        publishedDate: result.dateLastCrawled ? new Date(result.dateLastCrawled) : undefined,
        relevanceScore: undefined, // Bing doesn't provide relevance scores
      }));

      return {
        query,
        results,
        totalResults: data.webPages?.totalEstimatedMatches || results.length,
        metadata: {
          provider: 'bing',
          took: Date.now() - startTime,
          cached: false,
        },
      };
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Bing search failed: ${error.message}`);
      }
      throw new Error('Bing search failed with unknown error');
    }
  }

  /**
   * Test connection by performing a simple search
   */
  async testConnection(): Promise<boolean> {
    try {
      await this.search('test connection');
      return true;
    } catch (error) {
      console.error('Bing connection test failed:', error);
      return false;
    }
  }

  /**
   * Bing doesn't provide rate limit information in response headers
   */
  async getRateLimitInfo(): Promise<RateLimitInfo | null> {
    return null;
  }
}
